import { Button } from "@/components/ui/button";
import type { ReactNode } from "react";

interface ErrorStateProps {
  /** The title of the error message */
  title?: string;
  /** The error message to display */
  message?: string;
  /** The error object, will be used if message is not provided */
  error?: unknown;
  /** The icon to display above the error message */
  icon?: ReactNode;
  /** The action button text */
  actionText?: string;
  /** The action to perform when the button is clicked */
  onAction?: () => void;
  /** Additional CSS class names */
  className?: string;
  /** Container size - default is max-w-md */
  containerSize?: string;
}

/**
 * A reusable error state component that displays an error message and a retry button
 */
export function ErrorState({
  title = "Error",
  message,
  error,
  icon,
  actionText = "Try Again",
  onAction,
  className = "",
  containerSize = "max-w-md",
}: Readonly<ErrorStateProps>) {
  // Determine the error message to display
  const errorMessage =
    message ||
    (error instanceof Error ? error.message : "An unexpected error occurred");

  return (
    <div
      className={`container ${containerSize} mx-auto py-12 px-6 ${className}`}
    >
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-destructive/10 p-8 rounded-lg text-center w-full">
          {icon && <div className="justify-items-center mb-4">{icon}</div>}
          <h2 className="text-2xl font-bold text-destructive mb-2">{title}</h2>
          <p className="text-muted-foreground mb-6">{errorMessage}</p>
          {onAction && <Button onClick={onAction}>{actionText}</Button>}
        </div>
      </div>
    </div>
  );
}
